
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Grid2";

import { 
  Campaign,
  CampaignCharOptions,
  COptions,
  Receivable,
  useCampaignReceivableAlerts,
} from "@dndModels";

import API from "@utilities/smart_api";
import FlexBox from "@components/layout/FlexBox";
import CommonUtilities from "@utilities/common_utilities";
import { useGetWidthCategory } from "@utilities/common_hooks";
import { CALENDAR_OPTIONS, CALENDARS, findHolidaysForMonth, findSeasonsForMonth, getMMDDFromYearDay, mmddRangeString, parseDate } from "@dndModels/Calendars";
import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CalendarBox from "@components/input/calendar/CalendarBox";
import CampaignCharactersInput from "./characters/CampaignCharactersInput";
import CampaignPlayersInput from "./players/CampaignPlayersInput";
import CampaignInventoryInput from "./inventory/CampaignInventoryInput";
import CharacterOptionsInput from "@dndComponents/character/misc/CharacterOptionsInput";
import CampaignNPCsInput from "./npcs/CampaignNPCsInput";
import CampaignNotesInput from "./notes/CampaignNotesInput";
import CampaignEncountersInput from "./encounters/CampaignEncountersInput";
import QuickRulesControl from "./QuickRulesControl";
import CampaignQuestBoardsInput from "./quest_boards/CampaignQuestBoardsInput";
import TabMenu from "@components/input/menus/TabMenu";
import FloatMenu from "@components/input/menus/FloatMenu";
import LoadingBox from "@components/input/LoadingBox";
import { Fab, Tooltip } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import CharConnectUtilities from "@dndUtilities/char_connect_utilities";
import CampaignLettersInput from "./letters/CampaignLettersInput";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

interface Props {
  campaign: Campaign;
}

export default function CampaignParent({ campaign }: Props) {
  const { ref, widthCategory } = useGetWidthCategory();
  useCampaignReceivableAlerts(campaign._id, async (receivable) => {
    // swap this for your actual toast call
    // showToast(`${receivable.from_id} sent: ${receivable.message}`);
    if (receivable._id && !received_ids.includes(receivable._id)) {
      setReceivedIds([...received_ids, receivable._id]);
      console.log(receivable);
      const processed_group = await api.processResponse2<Receivable>({ all: [receivable], data_type: "receivable" });
      console.log(processed_group);
      if (processed_group.length === 1) {
        const processed = processed_group[0];
        if (processed.campaign_id === campaign._id && processed.from_id !== "DM" && processed.origin_id !== api.temp_id) {
          if (processed.temp && processed.message === "Refresh Campaign Inventory") {
            await api.loadCampaignInventory({ campaign });
            await connect_util.connectItems(campaign);
            campaign.subscription_holder.itemsChanged();
          }
        }
      }
    }
  });
  const [received_ids, setReceivedIds] = useState<string[]>([]);
  const [fetched, setFetched] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const getMenuOptions = () => {
    return api.user && api.user._id === campaign.owner_id
      ? ["Details","Characters","Players","Inventory","NPCs","Notes","Encounters","Quest Boards","Letters","Options","Rules"]
      : ["Characters","Players","Quest Boards","Letters","Options","Rules"];
  };

  const [tab, setTab] = useState(() => {
    const url_tab = searchParams.get("tab");
    // Note: getMenuOptions() depends on api.user, which should already be
    // populated by the time CampaignParent mounts (CampaignDetails gates on it)
    const menu = getMenuOptions();
    if (url_tab && menu.includes(url_tab)) {
      return url_tab;
    }
    return api.user && api.user._id === campaign.owner_id ? "Details" : "Characters";
  });
  const [ping, setPing] = useState(false);

  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
    }
  }, [fetched]);
  
  useEffect(() => {
    if (!fetched) return;
    const url_tab = searchParams.get("tab");
    if (url_tab && url_tab !== tab) {
      const menu = getMenuOptions();
      if (menu.includes(url_tab)) {
        setTab(url_tab);
      }
    }
  }, [searchParams]);
  
  const handleTabChange = useCallback((new_tab: string) => {
    setTab(new_tab);
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set("tab", new_tab);
      return params;
    }, { replace: true });
  }, [setSearchParams]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const saveCampaign = useCallback( async () => {
    setProcessing(true);
    const last_updated = common_util.get_time_number();
    campaign.last_updated = last_updated;
    if (campaign._id && campaign._id !== "") {
      await api.updateObject("campaign", campaign);
    } else {
      campaign.owner_id = api.user ? api.user._id : "No Login";
      await api.createObject("campaign", campaign);
    }
    setProcessing(false);
  }, [campaign]);

  const campaignChanged = useCallback(() => { 
    console.log("Campaign Save Tools - Changed");
    setTimeout(() => {
      saveCampaign();
    }, 100);
  }, []);

  useEffect(() => {
    console.log("Campaign Save Tools");
    campaign.subscription_holder.subscribe("Campaign", mounted, campaignChanged);
  }, [campaign, campaignChanged]);

  const renderMonthDetails = () => {
    const calendar = CALENDARS.find(o => o.true_id === campaign.calendar_id);
    const parsed_today = parseDate(campaign.current_date);
    const month = parsed_today.month;
    const current_month = calendar ? calendar.months.find(o => o.position === month) : undefined;
    if (calendar && current_month) {
      // debugger;
      const seasons = findSeasonsForMonth(calendar, month);
      const holidays = findHolidaysForMonth(calendar, month);
      
      return (
        <FlexBox direction="column">
          <FlexBox xs_expand={0} className="header centered padded">{ current_month.name }</FlexBox>
          <FlexBox xs_expand={0}>Season{ seasons.length > 1 ? "s" : "" }: { seasons.map(o => `${o.name} (${mmddRangeString({ mmdd_start: o.mmdd_start, mmdd_end: o.mmdd_end, calendar })})`).join(", ") }</FlexBox>
          { holidays.length > 0 &&
            <FlexBox xs_expand={0}>Holiday{ holidays.length > 1 ? "s" : "" }: { holidays.map(o => {
              const use_mmdd = o.mmdd ? o.mmdd : (o.year_day ? getMMDDFromYearDay({ calendar, year_day: o.year_day }) : -1);
              return `${o.name} (${mmddRangeString({ mmdd_start: use_mmdd, length: o.length, calendar })})`;
            }).join(", ") }</FlexBox>
          }
        </FlexBox>
      );
    }
    return null;
  }

  const renderTab = () => {
    const user = api.user;
    if (user) {
      if (tab === "Details") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid size={{ lg: 6, xs: 12 }}>
              <Grid container spacing={0} direction="row">
                <Grid size={6}>
                  <StringBox 
                    value={campaign.name} 
                    message={campaign.name.length > 0 ? "" : "Name Invalid"} 
                    name="Name"
                    onBlur={(changed: string) => {
                      const new_campaign = campaign;
                      new_campaign.name = changed;
                      new_campaign.changed();
                      setPing(!ping);
                    }}
                  />
                </Grid>
                <Grid size={6}>
                  <StringBox 
                    name="Team Name"
                    value={campaign.party_name} 
                    onBlur={(changed: string) => {
                      const new_campaign = campaign;
                      new_campaign.party_name = changed;
                      new_campaign.changed();
                      setPing(!ping);
                    }}
                  />
                </Grid>
                { campaign.calendar_id !== "" &&
                  <Grid size={12}>
                    <StringBox 
                      value={campaign.description} 
                      name="Description"
                      multiline
                      onChange={(changed: string) => {
                        const new_campaign = campaign;
                        new_campaign.description = changed;
                        new_campaign.changed();
                        setPing(!ping);
                      }}
                    />
                  </Grid>
                }
              </Grid>
            </Grid>
            <Grid size={{ lg: 6, xs: 12 }}>
              <Grid container spacing={0} direction="row">
                <Grid size={12}>
                  <SelectStringBox 
                    name="Calendar"
                    value={campaign.calendar_id} 
                    options={CALENDAR_OPTIONS}
                    onChange={(changed: string) => {
                      const new_campaign = campaign;
                      new_campaign.calendar_id = changed;
                      
                      new_campaign.changed();
                      setPing(!ping);
                    }}
                  />
                </Grid>
                { campaign.calendar_id !== "" &&
                  <>
                    <Grid size={{ md: 6, xs: 12 }} className="padded centered">
                      <CalendarBox 
                        name="Current Date"
                        calendar_id={campaign.calendar_id}
                        value={campaign.current_date} 
                        onChange={(changed) => {
                          const new_campaign = campaign;
                          new_campaign.current_date = changed;
                          
                          new_campaign.changed();
                          setPing(!ping);
                        }}
                      />
                    </Grid>
                    <Grid size={{ md: 6, xs: 12 }} className="padded">
                      { renderMonthDetails() }
                    </Grid>
                  </>
                }
              </Grid>
            </Grid>
            { campaign.calendar_id === "" &&
              <Grid size={12}>
                <StringBox 
                  value={campaign.description} 
                  name="Description"
                  multiline
                  onChange={(changed: string) => {
                    const new_campaign = campaign;
                    new_campaign.description = changed;
                    
                    new_campaign.changed();
                    setPing(!ping);
                  }}
                />
              </Grid>
            }
          </Grid>
        ); 
      } else if (tab === "Characters") {
        return (
          <CampaignCharactersInput
            campaign={campaign}
          />
        );
      } else if (tab === "Players") {
        return (
          <CampaignPlayersInput
            campaign={campaign}
          />
        ); 
      } else if (tab === "Inventory") {
        return (
          <CampaignInventoryInput
            campaign={campaign}
          /> 
        ); 
      } else if (tab === "Options") {
        return (
          <CharacterOptionsInput 
            obj={campaign}
            disabled={processing || !api.user || api.user._id !== campaign.owner_id}
            onChange={ async (options: COptions) => {
              // debugger;
              const new_campaign = campaign;
              let c_char_options = new_campaign.char_options;
              if (!c_char_options) {
                c_char_options = new CampaignCharOptions();
                c_char_options.campaign_id = new_campaign._id;
                c_char_options.campaign = new_campaign;
              } 
              c_char_options.char_options = options.clone();
              await api.upsertObject("campaign_char_options", c_char_options);
              new_campaign.char_options = c_char_options;
              new_campaign.last_updated = common_util.get_time_number();
              await api.upsertObject("campaign", new_campaign);
              
            }}
          />
        ); 
      } else if (tab === "NPCs") {
        return (
          <CampaignNPCsInput
            campaign={campaign}
          />
        ); 
      } else if (tab === "Notes") {
        return (
          <CampaignNotesInput
            campaign={campaign}
          />
        ); 
      } else if (tab === "Encounters") {
        return (
          <CampaignEncountersInput
            campaign={campaign}
          />
        );
      } else if (tab === "Rules") {
        return (
          <QuickRulesControl
            campaign={campaign}
          />
        ); 
      } else if (tab === "Quest Boards") {
        return (
          <CampaignQuestBoardsInput
            campaign={campaign}
          />
        ); 
      } else if (tab === "Letters") {
        return (
          <CampaignLettersInput
            campaign={campaign}
          />
        ); 
      }
    } 
    return (<>Coming Soon</>);
  }

  const renderMenu = () => {
    const menu = getMenuOptions();
    if (widthCategory === "lg") {
      return (
        <TabMenu selected={tab} onSelect={handleTabChange} options={menu} />
      );
    } else {
      return (
        <FloatMenu selected={tab} onSelect={handleTabChange} options={menu} />
      );
    }
  }
  
  const height = window.innerHeight - 64;
  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (!fetched) {
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} />);
  } else { 
    return (
      <FlexBox direction="column">
        <FlexBox direction="row">
          <FlexBox xs_expand={0} className="padded">
            <Tooltip title={`Back to Campaigns`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={ () => {
                  setNavigateTo(`/campaign`);
                }}>
                <ArrowBack/>
              </Fab>
            </Tooltip> 
          </FlexBox>
          <FlexBox xs_expand={0} className="header centered" style={{ width: "200px" }}>
            { campaign._id === "" ? "Create Campaign" : `Edit ${campaign.name}` }
          </FlexBox>
          <FlexBox>
            { renderMenu() }
          </FlexBox>
        </FlexBox>
        <FlexBox>
          { renderTab() }
        </FlexBox>
      </FlexBox>
    ); 
  }
}
